import { Tab } from '@headlessui/react';
import { ChartsByLabel, Latencies } from 'interfaces/performanceTest';
import { useEffect, useState } from 'react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  buildResponseTimePlots,
  commonXAxisProps,
  fillInTimeGaps,
  LabeledDataPoint,
} from './helpers';
import Loading from './Loading';
import TooltipContent from './TooltipContent';

interface Props {
  ticks: number[];
  charts: ChartsByLabel;
  loading: boolean;
}

const ResponseTime: React.FC<Props> = ({ ticks, charts, loading }) => {
  // const [overallDataPoints, setOverallDataPoints] = useState<DataPoint[]>();
  const [formattedDataPoints, setFormattedDataPoints] = useState<
    LabeledDataPoint<Latencies>[]
  >([]);
  const [labels, setLabels] = useState<string[]>([]);

  useEffect(() => {
    if (charts) {
      setFormattedDataPoints(
        fillInTimeGaps(
          buildResponseTimePlots(charts),
          (timeStamp) => ({ timeStamp, metricByLabel: {} }),
          (timeStamp, existing) => ({
            timeStamp,
            metricByLabel: existing.metricByLabel,
          })
        )
      );
      setLabels([
        'Overall',
        ...Object.keys(charts).filter((l) => l !== 'Overall'),
      ]);
    }
  }, [charts]);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <Tab.Group>
          <Tab.List>
            <header className="px-3 border-b-2 border-gray-100 md:flex md:justify-between">
              <span style={{ lineHeight: '3rem' }}>Response Time</span>
              <span className="inline-flex flex-wrap mb-2 md:mx-4 rounded-xl bg-summer-green-100 p-1">
                {labels.map((label, i) => (
                  <Tab
                    key={i}
                    style={{
                      maxWidth: '250px',
                    }}
                    className={({ selected }) =>
                      [
                        'overflow-hidden text-ellipsis md:max-w-md whitespace-nowrap',
                        'rounded-lg py-2.5 px-5 text-sm font-medium leading-5 text-summer-green-700',
                        selected
                          ? 'bg-white border border-summer-green-200 shadow'
                          : 'hover:bg-summer-green-200',
                      ].join(' ')
                    }
                  >
                    {label}
                  </Tab>
                ))}
              </span>
            </header>
          </Tab.List>
          <Tab.Panels>
            {labels.map((label, i) => (
              <Tab.Panel key={i}>
                <div className="h-44 my-5">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      width={500}
                      height={400}
                      data={formattedDataPoints}
                      margin={{
                        top: 10,
                        right: 30,
                        left: 10,
                        bottom: 10,
                      }}
                    >
                      <XAxis {...commonXAxisProps} ticks={ticks} />
                      <YAxis />
                      <Tooltip content={TooltipContent} />
                      <Line
                        dot={false}
                        type="monotone"
                        dataKey={(row) => row.metricByLabel[label]?.p50Ms}
                        name="p50 Latency (ms)"
                        stroke="#5CCCCE"
                      />
                      <Line
                        dot={false}
                        type="monotone"
                        dataKey={(row) => row.metricByLabel[label]?.p75Ms}
                        name="p75 Latency (ms)"
                        stroke="#38B8BA"
                      />
                      <Line
                        dot={false}
                        type="monotone"
                        dataKey={(row) => row.metricByLabel[label]?.p90Ms}
                        name="p90 Latency (ms)"
                        stroke="#5D8CB0"
                      />
                      <Line
                        dot={false}
                        type="monotone"
                        dataKey={(row) => row.metricByLabel[label]?.p95Ms}
                        name="p95 Latency (ms)"
                        stroke="#3D627E"
                      />
                      <Line
                        dot={false}
                        type="monotone"
                        dataKey={(row) => row.metricByLabel[label]?.p99Ms}
                        name="p99 Latency (ms)"
                        stroke="#304D63"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </Tab.Panel>
            ))}
          </Tab.Panels>
        </Tab.Group>
      )}
    </>
  );
};

export default ResponseTime;
